// @ts-nocheck — vendored ManualAnnotation camera-math; typechecked upstream, exempt from host strict flags (noUncheckedIndexedAccess/exactOptionalPropertyTypes).
// Parse the Double Sphere (ds) camera-model calibration bundle.
//
// Input shape (top-level wrapper, shared with parse_ocam.ts bundle format):
//   {
//     calibrations: [
//       {
//         calibration_text: "<YAML>",
//         calibration_time: "2026-05-20T12:46:25.591000",   // ISO 8601, string-sortable
//         calibration_reason: "...", calibration_result: "...",
//       },
//       ...
//     ],
//   }
//
// Each `calibration_text` is YAML of the form:
//   intrinsics:
//   - camera_name: videoFL
//     model_type: ds
//     params: [fx, fy, cx, cy, xi, alpha]
//     resolution: [W, H]
//     rate: 30
//   - camera_name: videoFR
//     ...
//   extrinsics_reference_cam: imu
//   extrinsics:
//   - camera_name: videoFL
//     transform_ref_current: [tx, ty, tz, qx, qy, qz, qw]    # Hamilton quaternion (qw last)
//   - camera_name: videoFR
//     ...
//   vignette:
//   - camera_name: videoFL
//     vignette_array: [...]                                  # ignored
//
// Camera-name → side mapping: videoFL→front_left, videoFR→front_right,
//                             videoL→left,        videoR→right.
//
// We select the latest entry by `calibration_time` whose YAML contains
// `model_type: ds`. FL+FR stereo R,T are derived by composing IMU-relative
// poses; side cameras (L, R) are wired similarly when present.

import { parse as parseYaml } from 'yaml';
import type { CalibrationData, DsParams } from '../../types';

type Mat3 = number[][];
type Vec3 = [number, number, number];

interface DsIntrinsic {
  camera_name: string;
  model_type: string;
  params: number[];
  resolution: [number, number];
}

interface DsExtrinsic {
  camera_name: string;
  transform_ref_current: number[]; // [tx, ty, tz, qx, qy, qz, qw]
}

interface DsYaml {
  intrinsics: DsIntrinsic[];
  extrinsics_reference_cam?: string;
  extrinsics: DsExtrinsic[];
}

interface BundleEntry {
  calibration_text?: string;
  calibration_time?: string;
}

export interface RawDsBundle {
  calibrations: BundleEntry[];
}

function mat3Mul(A: Mat3, B: Mat3): Mat3 {
  const C: Mat3 = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  for (let i = 0; i < 3; i++)
    for (let j = 0; j < 3; j++) for (let k = 0; k < 3; k++) C[i][j] += A[i][k] * B[k][j];
  return C;
}

function mat3Transpose(M: Mat3): Mat3 {
  return [
    [M[0][0], M[1][0], M[2][0]],
    [M[0][1], M[1][1], M[2][1]],
    [M[0][2], M[1][2], M[2][2]],
  ];
}

function mat3MulVec3(M: Mat3, v: Vec3): Vec3 {
  return [
    M[0][0] * v[0] + M[0][1] * v[1] + M[0][2] * v[2],
    M[1][0] * v[0] + M[1][1] * v[1] + M[1][2] * v[2],
    M[2][0] * v[0] + M[2][1] * v[1] + M[2][2] * v[2],
  ];
}

function quatToMat3(qx: number, qy: number, qz: number, qw: number): Mat3 {
  const xx = qx * qx,
    yy = qy * qy,
    zz = qz * qz;
  const xy = qx * qy,
    xz = qx * qz,
    yz = qy * qz;
  const wx = qw * qx,
    wy = qw * qy,
    wz = qw * qz;
  return [
    [1 - 2 * (yy + zz), 2 * (xy - wz), 2 * (xz + wy)],
    [2 * (xy + wz), 1 - 2 * (xx + zz), 2 * (yz - wx)],
    [2 * (xz - wy), 2 * (yz + wx), 1 - 2 * (xx + yy)],
  ];
}

// Apply the same side-camera 180° auto-correction as parse_ocam.ts:imuCamPose.
function imuCamPose(transform: number[]): { R: Mat3; t: Vec3 } {
  const [tx, ty, tz, qx, qy, qz, qw] = transform;
  let R = quatToMat3(qx, qy, qz, qw);
  if (R[1][1] < -0.5) {
    R = [
      [-R[0][0], -R[0][1], R[0][2]],
      [-R[1][0], -R[1][1], R[1][2]],
      [-R[2][0], -R[2][1], R[2][2]],
    ];
  }
  return { R, t: [tx, ty, tz] };
}

// Compose stereo transform src → dst from IMU-relative poses.
function composeStereo(src: { R: Mat3; t: Vec3 }, dst: { R: Mat3; t: Vec3 }): { R: Mat3; T: Vec3 } {
  const Rdt = mat3Transpose(dst.R);
  const R = mat3Mul(Rdt, src.R);
  const diff: Vec3 = [src.t[0] - dst.t[0], src.t[1] - dst.t[1], src.t[2] - dst.t[2]];
  const T = mat3MulVec3(Rdt, diff);
  return { R, T };
}

function isDsParams(p: unknown): p is DsParams {
  return (
    Array.isArray(p) &&
    p.length === 6 &&
    p.every((n) => typeof n === 'number' && Number.isFinite(n))
  );
}

function isDsYaml(v: unknown): v is DsYaml {
  if (!v || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  return Array.isArray(o.intrinsics) && Array.isArray(o.extrinsics);
}

function hasDsModel(y: DsYaml): boolean {
  return y.intrinsics.some((i) => i.model_type === 'ds');
}

// Find the latest calibration_text entry (by calibration_time) whose YAML
// contains a DS intrinsic block. Returns the parsed YAML.
function pickLatestDsYaml(raw: RawDsBundle): DsYaml | null {
  let best: { time: string; yaml: DsYaml } | null = null;
  for (const entry of raw.calibrations) {
    if (typeof entry.calibration_text !== 'string') continue;
    let parsed: unknown;
    try {
      parsed = parseYaml(entry.calibration_text);
    } catch {
      continue;
    }
    if (!isDsYaml(parsed) || !hasDsModel(parsed)) continue;
    const time = entry.calibration_time ?? '';
    if (!best || time > best.time) best = { time, yaml: parsed };
  }
  return best?.yaml ?? null;
}

// Detection helper used by the dispatcher in index.ts. Returns true if the
// bundle has at least one DS calibration entry — distinguishes from Scaramuzza
// bundles which carry `calibration_json.cameras` instead.
export function bundleHasDs(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const cals = (raw as { calibrations?: unknown }).calibrations;
  if (!Array.isArray(cals)) return false;
  return pickLatestDsYaml({ calibrations: cals as BundleEntry[] }) !== null;
}

// videoFL → front_left, videoFR → front_right, videoL → left, videoR → right.
// Match strictly to avoid videoFL falsely satisfying the videoL pattern.
function classifyCamera(name: string): 'fl' | 'fr' | 'l' | 'r' | null {
  if (/^video?fl$/i.test(name) || /^head_front_left$/i.test(name)) return 'fl';
  if (/^video?fr$/i.test(name) || /^head_front_right$/i.test(name)) return 'fr';
  if (/^video?l$/i.test(name) || /^head_left$/i.test(name)) return 'l';
  if (/^video?r$/i.test(name) || /^head_right$/i.test(name)) return 'r';
  return null;
}

export function parseDsBundle(raw: RawDsBundle): CalibrationData {
  if (!Array.isArray(raw.calibrations) || raw.calibrations.length === 0) {
    throw new Error('DS calibration: no `calibrations` entries');
  }
  const yaml = pickLatestDsYaml(raw);
  if (!yaml) {
    throw new Error('DS calibration: no entry with `model_type: ds` found');
  }

  const intrinsicByKind: Partial<Record<'fl' | 'fr' | 'l' | 'r', DsIntrinsic>> = {};
  for (const intr of yaml.intrinsics) {
    if (intr.model_type !== 'ds') continue;
    if (!isDsParams(intr.params)) continue;
    const kind = classifyCamera(intr.camera_name);
    if (kind) intrinsicByKind[kind] = intr;
  }
  if (!intrinsicByKind.fl || !intrinsicByKind.fr) {
    throw new Error(
      `DS calibration: missing FL/FR intrinsics (found cameras: ${yaml.intrinsics.map((i) => i.camera_name).join(', ')})`
    );
  }

  const poseByKind: Partial<Record<'fl' | 'fr' | 'l' | 'r', { R: Mat3; t: Vec3 }>> = {};
  for (const ext of yaml.extrinsics) {
    if (!Array.isArray(ext.transform_ref_current) || ext.transform_ref_current.length !== 7)
      continue;
    const kind = classifyCamera(ext.camera_name);
    if (kind) poseByKind[kind] = imuCamPose(ext.transform_ref_current);
  }
  if (!poseByKind.fl || !poseByKind.fr) {
    throw new Error('DS calibration: missing FL/FR extrinsics');
  }

  const flCam = intrinsicByKind.fl;
  const frCam = intrinsicByKind.fr;
  const { R, T } = composeStereo(poseByKind.fl, poseByKind.fr);

  const result: CalibrationData = {
    cameraModel: 'ds',
    R,
    T,
    flSize: [flCam.resolution[0], flCam.resolution[1]],
    frSize: [frCam.resolution[0], frCam.resolution[1]],
    flDs: flCam.params as DsParams,
    frDs: frCam.params as DsParams,
  };

  if (intrinsicByKind.l && poseByKind.l) {
    const lCam = intrinsicByKind.l;
    const stereo = composeStereo(poseByKind.fl, poseByKind.l);
    result.lDs = lCam.params as DsParams;
    result.lSize = [lCam.resolution[0], lCam.resolution[1]];
    result.R_fl_l = stereo.R;
    result.T_fl_l = stereo.T;
  }
  if (intrinsicByKind.r && poseByKind.r) {
    const rCam = intrinsicByKind.r;
    const stereo = composeStereo(poseByKind.fr, poseByKind.r);
    result.rDs = rCam.params as DsParams;
    result.rSize = [rCam.resolution[0], rCam.resolution[1]];
    result.R_fr_r = stereo.R;
    result.T_fr_r = stereo.T;
  }

  return result;
}
